import React, { useRef, useState } from 'react';
import '../../styles/hub-v2.css';
import APIService from '../../services/APIService';

/*
 * HubNodeEditor — author or edit a page in YOUR hub.
 *
 * Writing model (no markdown to learn): each tab is a plain textarea where
 * every LINE becomes a bullet, and a line written as "Term — description"
 * becomes a bold-term entry. Two AI assists, both returning drafts for review
 * (never auto-saved): "Draft with AI" fills the page from pasted material,
 * "Generate questions" writes the What-you'll-be-asked set from the page.
 */

const pointsToText = (pts) => (pts || [])
  .map((p) => (typeof p === 'string' ? p : `${p.t} — ${p.d}`)).join('\n');

const textToPoints = (txt) => String(txt || '').split('\n')
  .map((l) => l.trim()).filter(Boolean)
  .map((l) => {
    const m = l.match(/^(.{2,60}?)\s+(?:—|--)\s+(.+)$/);
    return m ? { t: m[1], d: m[2] } : l;
  });

// Default persistence: the learner's own hub. The Creator Studio reuses this
// editor for PACK pages by passing an `api` with the same contract:
//   create(payload)→{node}  update(id,payload)→{node}
//   compose(name,brief)→{doc}  generateQuestions(id)→{questions}
const USER_HUB_API = {
  create: (payload) => APIService.createUserHubNode(payload),
  update: (id, payload) => APIService.updateUserHubNode(id, payload),
  // A short BRIEF ("compare X vs Y") drafts via /compose; real pasted MATERIAL
  // (80+ chars) goes through ingestion so the draft arrives WITH its questions.
  // Clamped to the server's 50k-char cap — long material is truncated, not rejected.
  compose: (name, brief) => (brief.trim().length < 80
    ? APIService.composeUserHubPage(name, brief)
    : APIService.ingestHubText(name, brief.slice(0, 50000))),
  generateQuestions: (id) => APIService.generateUserHubQuestions(id),
};

// client-side mirrors of the server caps — fail fast with a friendly message
const MAX_IMG_BYTES = 8 * 1024 * 1024;
const MAX_PDF_BYTES = 15 * 1024 * 1024;
const MAX_TXT_BYTES = 512 * 1024;
const MAX_FILES = 5;

const isImage = (f) => f.type.startsWith('image/')
  || /\.(png|jpe?g|webp|gif)$/i.test(f.name);

// Detected upload kind → its client-side size cap (mirrors the server).
const fileKind = (f) => {
  const ext = (f.name.split('.').pop() || '').toLowerCase();
  if (isImage(f)) return 'image';
  if (ext === 'pdf' || f.type === 'application/pdf') return 'pdf';
  if (ext === 'txt' || ext === 'md' || f.type.startsWith('text/')) return 'text';
  return null;
};
const KIND_MAX = { image: MAX_IMG_BYTES, pdf: MAX_PDF_BYTES, text: MAX_TXT_BYTES };
const KIND_LIMIT_LABEL = { image: '8 MB', pdf: '15 MB', text: '512 KB' };

const fileToB64 = (f) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('Could not read the file.'));
  r.readAsDataURL(f);
});

const sourceLine = (src) => {
  if (!src) return '';
  const clipped = src.used_chars && src.chars > src.used_chars
    ? ` (drafted from the first ${src.used_chars.toLocaleString()} characters)` : '';
  if (src.type === 'pdf') {
    const pages = src.pages_read < src.pages
      ? `${src.pages_read} of ${src.pages} pages` : `${src.pages} page${src.pages === 1 ? '' : 's'}`;
    return `Read your PDF (${pages})${clipped}`;
  }
  if (src.type === 'files') {
    const k = src.kinds || {};
    const bits = [[k.image, 'image'], [k.pdf, 'PDF'], [k.text, 'text file']]
      .filter(([n]) => n > 0).map(([n, w]) => `${n} ${w}${n > 1 ? (w === 'PDF' ? 's' : 's') : ''}`);
    return `Read ${src.read || src.count} file${(src.read || src.count) === 1 ? '' : 's'}${bits.length ? ` (${bits.join(', ')})` : ''}${clipped}`;
  }
  return `Read ${(src.chars || 0).toLocaleString()} characters${clipped}`;
};

const HubNodeEditor = ({ node, onSaved, onCancel, api = USER_HUB_API, heading,
  topics = null, initialParentId = null, onNewTopic = null, onDeleted = null }) => {
  // topics: [{id, name, subs: [{id, name}]}] — the user's own topic tree
  // savedNode tracks the persisted identity: after the first save (including
  // the implicit one before generate-questions) every later save is an UPDATE.
  const [savedNode, setSavedNode] = useState(node || null);
  const isNew = !savedNode;
  const [name, setName] = useState(node?.name || '');
  // (savedNode above is the persistence identity; `node` is only the initial value)
  const [intro, setIntro] = useState(node?.doc?.intro || '');
  const [tabs, setTabs] = useState(() => (node?.doc?.tabs?.length
    ? node.doc.tabs.map((t) => (t.md
      ? { id: t.id, label: t.label, text: t.md, isMd: true }
      : { id: t.id, label: t.label, text: pointsToText(t.points) }))
    : [{ id: 'overview', label: 'Overview', text: '' }]));
  const [active, setActive] = useState(0);
  const [questions, setQuestions] = useState(node?.questions || []);
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [srcInfo, setSrcInfo] = useState('');
  const [dirty, setDirty] = useState(false);
  const [topicList, setTopicList] = useState(topics || []);
  // resolve an initial parent (topic OR subtopic id) into the two levels
  const initialParent = node?.parent_id ?? initialParentId ?? '';
  const findLevels = (pid, list) => {
    if (!pid) return { t: '', s: '' };
    for (const t of list) {
      if (t.id === pid) return { t: t.id, s: '' };
      if ((t.subs || []).some((x) => x.id === pid)) return { t: t.id, s: pid };
    }
    return { t: '', s: '' };
  };
  const [locTopic, setLocTopic] = useState(() => findLevels(initialParent, topics || []).t);
  const [locSub, setLocSub] = useState(() => findLevels(initialParent, topics || []).s);
  const parentId = locSub || locTopic || '';
  const [newIn, setNewIn] = useState(null);        // null | 'topic' | 'sub'
  const [newName, setNewName] = useState('');
  const fileRef = useRef(null);

  const addLocation = async (level) => {
    const trimmed = newName.trim();
    if (!trimmed || !onNewTopic) return;
    try {
      if (level === 'topic') {
        const t = await onNewTopic(trimmed, 'topic', null);
        setTopicList((l) => [...l, { id: t.id, name: t.name, subs: [] }]);
        setLocTopic(t.id); setLocSub('');
      } else {
        const s = await onNewTopic(trimmed, 'subtopic', locTopic);
        setTopicList((l) => l.map((t) => (t.id === locTopic
          ? { ...t, subs: [...(t.subs || []), { id: s.id, name: s.name }] } : t)));
        setLocSub(s.id);
      }
      setDirty(true);
      setNewIn(null);
      setNewName('');
    } catch (ex) { setErr(ex.message); }
  };

  const docFromState = () => ({
    intro,
    tabs: tabs.filter((t) => t.label.trim())
      .map((t, i) => (t.isMd
        ? { id: t.id || `tab${i + 1}`, label: t.label.trim(), points: [], md: t.text }
        : { id: t.id || `tab${i + 1}`, label: t.label.trim(), points: textToPoints(t.text) })),
  });

  const setTab = (i, patch) => { setDirty(true); setTabs((ts) => ts.map((t, k) => (k === i ? { ...t, ...patch } : t))); };
  const addTab = () => { setDirty(true); setTabs((ts) => [...ts, { id: `tab${ts.length + 1}`, label: 'New tab', text: '' }]); setActive(tabs.length); };
  const removeTab = (i) => { setDirty(true); setTabs((ts) => ts.filter((_, k) => k !== i)); setActive((a) => Math.max(0, a - (i <= a ? 1 : 0))); };

  // #10: the replace-confirm runs BEFORE the model call — a declined confirm
  // must never cost the user a quota slot and a finished draft.
  const confirmReplace = () => {
    const hasContent = intro.trim() || tabs.some((t) => t.text.trim()) || questions.length > 0;
    return !hasContent
      || window.confirm('The AI draft will replace the current intro, tabs and questions. Continue? Nothing is saved until you click Save.');
  };

  // Apply a draft {doc, questions?, source?} into the editor — REVIEW-first:
  // nothing persists until Save.
  const applyDraft = (d) => {
    setDirty(true);
    setIntro(d.doc?.intro || '');
    setTabs((d.doc?.tabs || []).map((t) => (t.md
      ? { id: t.id, label: t.label, text: t.md, isMd: true }
      : { id: t.id, label: t.label, text: pointsToText(t.points) })));
    if (d.questions) setQuestions(d.questions);
    setActive(0);
    const parts = [`${(d.doc?.tabs || []).length} tabs`];
    if (d.questions) parts.push(`${d.questions.length} questions`);
    const corrected = d.source?.issues_fixed ? ' · auto-corrected' : '';
    setSrcInfo(`${sourceLine(d.source) || 'Drafted'} → ${parts.join(' + ')}${corrected}. Review below, edit anything, then Save.`);
    return true;
  };

  const draftWithAI = async () => {
    if (!name.trim() || !brief.trim()) { setErr('Give the page a heading and paste some material first.'); return; }
    if (!confirmReplace()) return;
    setBusy('draft'); setErr(''); setSrcInfo('');
    try {
      applyDraft(await api.compose(name.trim(), brief));
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  // Up to 5 files of any mix (images · PDFs · text). Each is extracted to clean
  // text, combined, then organized → checked → questioned by the pipeline.
  const ingestFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!name.trim()) { setErr('Give the page a heading first — the notes are structured around it.'); return; }
    if (files.length > MAX_FILES) { setErr(`Up to ${MAX_FILES} files at a time.`); return; }
    for (const f of files) {
      const kind = fileKind(f);
      if (!kind) { setErr(`"${f.name}" isn't a supported type — upload images (PNG/JPEG/WebP), PDFs, or .txt/.md files.`); return; }
      if (f.size > KIND_MAX[kind]) { setErr(`"${f.name}" is over the ${KIND_LIMIT_LABEL[kind]} limit for ${kind} files.`); return; }
    }
    if (!confirmReplace()) return;
    setBusy('ingest'); setErr(''); setSrcInfo('');
    try {
      const payload = await Promise.all(files.map(async (f) => ({ filename: f.name, content_b64: await fileToB64(f) })));
      applyDraft(await APIService.ingestHubFiles(name.trim(), payload));
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  const save = async (thenGenerate = false) => {
    if (!name.trim()) { setErr('The page needs a heading.'); return null; }
    setBusy('save'); setErr('');
    try {
      const payload = { name: name.trim(), doc: docFromState(), questions };
      if (topics !== null) payload.parent_id = parentId || '';
      const { node: saved } = isNew
        ? await api.create(payload)
        : await api.update(savedNode.id, payload);
      setSavedNode(saved);
      setDirty(false);
      if (thenGenerate) return saved;
      onSaved(saved);
      return saved;
    } catch (e) { setErr(e.message); return null; } finally { if (!thenGenerate) setBusy(''); }
  };

  const generateQuestions = async () => {
    setErr('');
    const saved = await save(true);            // questions are grounded in the SAVED page
    if (!saved) { setBusy(''); return; }
    setBusy('questions');
    try {
      const { questions: qs } = await api.generateQuestions(saved.id);
      setQuestions(qs);
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  return (
    <div className="he-wrap">
      <div className="he-head">
        <h2>{heading || (isNew ? 'New page in your hub' : 'Edit your page')}</h2>
        <div className="he-actions">
          <button className="h2-ghost" type="button" disabled={!!busy}
            onClick={() => { if (dirty && !window.confirm('Discard your unsaved changes to this page?')) return; onCancel(); }}>
            Cancel
          </button>
          <button className="h2-primary" type="button" onClick={() => save(false)} disabled={!!busy}>
            {busy === 'save' ? 'Saving…' : 'Save page'}
          </button>
        </div>
      </div>
      {node?.forked_from && (
        <div className="he-note">Your own copy — the shared original stays untouched.</div>
      )}
      {err && <div className="he-err">{err}</div>}

      <section className="he-card">
      <label className="he-lbl" htmlFor="he-name">Heading</label>
      <input id="he-name" className="he-input" value={name} onChange={(e) => { setDirty(true); setName(e.target.value); }}
        placeholder="e.g. Retrieval Methods (my edition)" maxLength={120} />

      {topics !== null && (
        <div className="he-loc">
          <label className="he-lbl">Location</label>
          <div className="he-chips">
            <button type="button" className={`he-chip${!locTopic ? ' on' : ''}`}
              onClick={() => { setDirty(true); setLocTopic(''); setLocSub(''); }}>
              Unfiled
            </button>
            {topicList.map((t) => (
              <button key={t.id} type="button" className={`he-chip${locTopic === t.id ? ' on' : ''}`}
                onClick={() => { setDirty(true); setLocTopic(t.id); setLocSub(''); }}>
                {t.name}
              </button>
            ))}
            {onNewTopic && (newIn === 'topic' ? (
              <span className="he-chip-input">
                <input autoFocus value={newName} maxLength={60} placeholder="Topic name"
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setNewIn(null); if (e.key === 'Enter') addLocation('topic'); }} />
                <button type="button" onClick={() => addLocation('topic')} disabled={!newName.trim()}>✓</button>
              </span>
            ) : (
              <button type="button" className="he-chip new" onClick={() => { setNewIn('topic'); setNewName(''); }}>
                + topic
              </button>
            ))}
          </div>
          {locTopic && (
            <div className="he-chips sub">
              <button type="button" className={`he-chip${!locSub ? ' on' : ''}`}
                onClick={() => { setDirty(true); setLocSub(''); }}>
                directly under {topicList.find((t) => t.id === locTopic)?.name || 'topic'}
              </button>
              {(topicList.find((t) => t.id === locTopic)?.subs || []).map((s) => (
                <button key={s.id} type="button" className={`he-chip${locSub === s.id ? ' on' : ''}`}
                  onClick={() => { setDirty(true); setLocSub(s.id); }}>
                  {s.name}
                </button>
              ))}
              {onNewTopic && (newIn === 'sub' ? (
                <span className="he-chip-input">
                  <input autoFocus value={newName} maxLength={60} placeholder="Subtopic name"
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setNewIn(null); if (e.key === 'Enter') addLocation('sub'); }} />
                  <button type="button" onClick={() => addLocation('sub')} disabled={!newName.trim()}>✓</button>
                </span>
              ) : (
                <button type="button" className="he-chip new" onClick={() => { setNewIn('sub'); setNewName(''); }}>
                  + subtopic
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <label className="he-lbl" htmlFor="he-intro">Introduction</label>
      <textarea id="he-intro" className="he-textarea intro" value={intro} onChange={(e) => { setDirty(true); setIntro(e.target.value); }}
        placeholder="One or two sentences on why this concept matters." maxLength={600} rows={2} />
      </section>

      <div className="he-ai">
        <label className="he-lbl" htmlFor="he-brief">Bring your material (optional) — the AI turns it into notes + questions</label>
        <textarea id="he-brief" className="he-textarea" rows={4} value={brief} onChange={(e) => setBrief(e.target.value)}
          placeholder="Paste your raw notes, a doc, or a brief — the agent will organize it into the page below for your review." />
        <div className="he-ai-row">
          <button className="h2-ghost" type="button" onClick={draftWithAI} disabled={!!busy}>
            {busy === 'draft' ? '✦ Drafting…' : '✦ Draft from this text'}
          </button>
          <button className="h2-ghost" type="button" onClick={() => fileRef.current?.click()} disabled={!!busy}
            title="Up to 5 files, mixed — photos of notes, PDFs, and text files. They're read, organized, checked, and combined into one page. 20 uploads a day.">
            {busy === 'ingest'
              ? '✦ Reading, organizing & checking…'
              : '📎 Upload notes — up to 5 files (images · PDFs · text)'}
          </button>
          <input ref={fileRef} type="file" hidden multiple
            accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.txt,.md,image/*,application/pdf,text/plain,text/markdown"
            onChange={(e) => { const fs = e.target.files; e.target.value = ''; ingestFiles(fs); }} />
        </div>
        {busy === 'ingest' && <div className="he-note">Reading your files, organizing the notes, checking them, and writing questions — this can take up to a minute.</div>}
        {srcInfo && <div className="he-src">✓ {srcInfo}</div>}
      </div>

      <section className="he-card">
      <label className="he-lbl">Content — each tab is a section of the page</label>
      <div className="he-tabs">
        {tabs.map((t, i) => (
          <span key={i} className={`he-tab${i === active ? ' on' : ''}`}>
            <button type="button" onClick={() => setActive(i)}>{t.label || '…'}</button>
            {tabs.length > 1 && (
              <button type="button" className="he-tab-x" onClick={() => removeTab(i)} aria-label={`Remove ${t.label}`}>×</button>
            )}
          </span>
        ))}
        <button type="button" className="he-tab-add" onClick={addTab}>+ Tab</button>
      </div>
      {tabs[active] && (
        <div className="he-tabpane">
          <input className="he-input sm" value={tabs[active].label} maxLength={40}
            onChange={(e) => setTab(active, { label: e.target.value })} placeholder="Tab name" />
          <textarea className="he-textarea" rows={tabs[active].isMd ? 14 : 9} value={tabs[active].text}
            onChange={(e) => setTab(active, { text: e.target.value })}
            placeholder={tabs[active].isMd
              ? 'Markdown notes — ## headings, ### subheadings, bullets, | tables |.'
              : 'One point per line. Write "Term — description" to get a bold term.\nExample:\nBM25 — sparse ranking with TF, IDF and length normalization.\nExact-term matching wins on IDs and rare names.'} />
          {tabs[active].isMd && <div className="he-note">This tab is markdown notes — headings, bullets and | tables | render on the page.</div>}
        </div>
      )}

      </section>

      <section className="he-card">
      <div className="he-qhead">
        <label className="he-lbl">What you'll be asked</label>
        <button className="h2-ghost" type="button" onClick={generateQuestions} disabled={!!busy}
          title="Saves the page first — the questions are grounded in the saved content">
          {busy === 'questions' ? '✦ Writing questions…' : busy === 'save' ? 'Saving…' : '✦ Save + generate questions'}
        </button>
      </div>
      {questions.length === 0 ? (
        <div className="he-note">No questions yet — generate them from your content, or add your own below. Practice modes serve these.</div>
      ) : (
        <ul className="he-qs">
          {questions.map((q, i) => (
            <li key={q.id || i}>
              <span className="he-qdepth">d{q.depth || 1}</span>
              <input className="he-input sm grow" value={q.question} maxLength={500}
                onChange={(e) => { setDirty(true); setQuestions((qs) => qs.map((x, k) => (k === i ? { ...x, question: e.target.value } : x))); }} />
              <button type="button" className="he-tab-x" onClick={() => { setDirty(true); setQuestions((qs) => qs.filter((_, k) => k !== i)); }} aria-label="Remove question">×</button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="h2-mini" onClick={() => { setDirty(true); setQuestions((qs) => [...qs, { question: '', depth: 1 }]); }}>
        + Add a question
      </button>

      {savedNode && onDeleted && (
        <div className="he-danger">
          <button type="button" className="he-delete" disabled={!!busy}
            onClick={async () => {
              if (!window.confirm(`Delete "${name || 'this page'}"? Its questions and practice history stay in your memory log, but the page is gone for good.`)) return;
              try {
                await APIService.deleteUserHubNode(savedNode.id);
                onDeleted();
              } catch (ex) { setErr(ex.message); }
            }}>
            Delete this page
          </button>
        </div>
      )}
      </section>
    </div>
  );
};

export default HubNodeEditor;
